import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Uma callback de identidade ESTÁVEL que sempre executa a versão COMITADA mais
 * recente.
 *
 * O mesmo papel do `useEffectEvent` do React — ler estado/props frescos sem
 * entrar nas deps de um efeito — mas SEM a restrição dele: Effect Event só pode
 * ser chamado de dentro de um Effect, e a doc do React proíbe passá-lo adiante
 * ("Do not call them during rendering or pass them to other components or
 * Hooks"). Aqui o retorno pode virar prop de botão, sair de um hook customizado
 * ou entrar em outro hook, que é exatamente o que o mapa de hotéis precisa.
 *
 * A troca acontece no COMMIT, não no render: um render de transição que suspende
 * nunca é comitado, e escrever a ref durante o render deixaria a callback
 * executando a closure desse render descartado — quem chamasse depois (um evento
 * do Mapbox, um clique) leria estado que nunca chegou à tela. `useLayoutEffect`
 * porque nenhum chamador executa dentro do mesmo commit: todos são evento de
 * usuário ou callback registrada em efeito.
 */
export function useStableCallback<A extends readonly unknown[], R>(
  fn: (...args: A) => R,
): (...args: A) => R {
  const latest = useRef(fn);
  useLayoutEffect(() => {
    latest.current = fn;
  }, [fn]);
  return useCallback((...args: A) => latest.current(...args), []);
}
